/**
 * Shared EmptyState component
 *
 * Fills the available vertical space of its parent via `flex-1`. The parent
 * must be a constrained-height flex column (e.g. `h-full flex flex-col`) for
 * this to fit the viewport without producing a scrollbar.
 *
 * @param {React.ReactNode|string} illustration - React node (blob JSX) or image src string
 * @param {string} [illustrationClassName] - Classes for <img> when illustration is a string (default: "w-[500px]")
 * @param {string} heading - Heading text
 * @param {string} description - Description text
 * @param {React.ReactNode} [actions] - Optional action buttons
 * @param {string} [minHeight] - Optional CSS minHeight escape hatch. Leave unset for flex-based sizing.
 * @param {"default"|"compact"} [variant] - "compact" for smaller contexts (justify-start, smaller text)
 * @param {string} [className] - Additional container classes
 */
const EmptyState = ({
  illustration,
  illustrationClassName = "w-[500px]",
  heading,
  description,
  actions,
  minHeight,
  variant = "default",
  className = "",
}) => {
  const isCompact = variant === "compact";

  return (
    <div
      className={`flex w-full flex-col items-center ${
        isCompact ? "justify-start" : "flex-1 min-h-0 justify-start "
      } text-center ${className}`}
      style={{ gap: "24px", ...(minHeight ? { minHeight } : {}) }}
    >
      {illustration &&
        (typeof illustration === "string" ? (
          <img src={illustration} alt="" className={illustrationClassName} />
        ) : (
          illustration
        ))}

      <div className="flex flex-col gap-2 items-center w-full max-w-[604px]">
        <p
          className={`font-semibold text-center w-full ${
            isCompact ? "text-[16px] text-app-text" : ""
          }`}
        >
          {heading}
        </p>
        <p
          className={`text-center w-full text-app-text-secondary ${
            isCompact ? "text-[12px] max-w-md" : "text-[12px]"
          }`}
        >
          {description}
        </p>
      </div>

      {actions && (
        <div className="flex justify-center gap-6 w-full max-w-[604px]">{actions}</div>
      )}
    </div>
  );
};

export default EmptyState;
